import { Tag, type TagJson, type TagsResponse } from '@ironsight/types'
import { computed, Ref } from 'vue'
import { useQuery, UseQueryReturnType } from '@tanstack/vue-query'
import { AxiosError, AxiosResponse } from 'axios'
import { getPaginatedTags } from '@/api/tags'

const DEFAULT_OFFSET = 0
const DEFAULT_LIMIT = 100

export function usePaginatedTagsQuery(
  offset?: Ref<number>,
  limit?: Ref<number>,
): UseQueryReturnType<Tag[], AxiosError> {
  return useQuery({
    queryKey: computed(() =>
      !offset && !limit ? ['get_all_tags'] : ['get_tags', offset?.value, limit?.value],
    ),
    queryFn: async ({ signal }) => {
      if (!offset && !limit) {
        const allTags: Tag[] = []
        let currentOffset = DEFAULT_OFFSET
        const pageSize = DEFAULT_LIMIT
        let hasMore = true

        while (hasMore) {
          const response: AxiosResponse<TagsResponse, AxiosError> = await getPaginatedTags(
            currentOffset,
            pageSize,
            signal,
          )

          const tags = response.data.data.map((json: TagJson) => new Tag(json))
          allTags.push(...tags)

          hasMore = response.data.data.length === pageSize
          currentOffset += pageSize
        }

        return allTags
      } else {
        const response: AxiosResponse<TagsResponse, AxiosError> = await getPaginatedTags(
          offset?.value,
          limit?.value,
          signal,
        )

        return response.data.data.map((json: TagJson) => new Tag(json))
      }
    },
    retry: false,
    keepPreviousData: !offset && !limit,
  })
}
